import { memo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input } from '@shared/components';
import { DatePicker } from '@shared/components/ui/date-picker';
import { Label } from '@shared/components/ui/label';
import type { CreateEntryFormData } from '../../models/CreateEntrySchema';

interface EntryDetailsFormProps {
  /** Si está en estado de carga */
  isLoading?: boolean;
  /** Si está deshabilitado */
  disabled?: boolean;
  /** Título de la sección */
  title?: string;
  /** Descripción de la sección */
  description?: string;
}

const EntryDetailsForm = memo<EntryDetailsFormProps>(({ isLoading = false, disabled = false, title, description }) => {
  const { t } = useTranslation('merchandiseV3');
  const {
    register,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<CreateEntryFormData>();

  // Funciones estables para DatePicker
  const handleDateChange = useCallback(
    (date: Date | null) => {
      if (date) {
        // Formatear fecha en zona horaria local para evitar problemas de UTC
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const localDateString = `${year}-${month}-${day} ${hours}:${minutes}:00`;
        setValue('estimated_delivery_at', localDateString);
      } else {
        setValue('estimated_delivery_at', '');
      }
      trigger('estimated_delivery_at');
    },
    [setValue, trigger],
  );

  // Obtener fecha actual solo cuando sea necesario
  const currentDate = watch('estimated_delivery_at');
  const dateValue = currentDate ? new Date(currentDate.replace(' ', 'T')) : undefined;

  // Usar traducciones o valores por defecto
  const sectionTitle = title || t('form.details.title', 'Información de la entrada');
  const sectionDescription = description || t('form.details.description', 'Completa los datos básicos de la entrada');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
      {/* Columna izquierda - Título de la sección */}
      <div className="lg:col-span-2">
        <div className="sticky top-6">
          <h2 className="text-lg font-semibold text-foreground mb-3">{sectionTitle}</h2>
          <p className="text-sm text-muted-foreground">{sectionDescription}</p>
        </div>
      </div>

      {/* Columna derecha - Campos del formulario */}
      <div className="lg:col-span-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Fecha Estimada de Llegada */}
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="estimated_delivery_at">
              {t('form.details.estimated_delivery_label', 'Fecha estimada de entrega')}
            </Label>
            <DatePicker
              id="estimated_delivery_at"
              date={dateValue}
              onDateChange={handleDateChange as (date: Date | undefined) => void}
              placeholder={t('form.details.estimated_delivery_placeholder', 'Selecciona una fecha')}
              error={!!errors.estimated_delivery_at}
              disabled={disabled || isLoading}
              data-testid="entry-estimated-delivery-input"
            />
            {errors.estimated_delivery_at && (
              <p className="text-sm text-destructive" data-testid="estimated-delivery-error">
                {errors.estimated_delivery_at.message}
              </p>
            )}
          </div>

          {/* Número de Cajas */}
          <div className="space-y-2">
            <Label htmlFor="boxes_count" className="block text-sm font-medium text-foreground">
              {t('form.details.boxes_count_label', 'Número de cajas')}
            </Label>
            <Input
              id="boxes_count"
              type="number"
              min="1"
              placeholder={t('form.details.boxes_count_placeholder', 'Ej: 10')}
              data-testid="entry-boxes-count-input"
              disabled={disabled || isLoading}
              autoComplete="off"
              {...register('boxes_count', {
                valueAsNumber: true,
                setValueAs: (value) => (value === '' ? null : Number(value)),
              })}
            />
            {errors.boxes_count && (
              <p className="text-sm text-destructive" data-testid="boxes-count-error">
                {errors.boxes_count.message}
              </p>
            )}
          </div>

          {/* Código de Tracking */}
          <div className="space-y-2">
            <Label htmlFor="tracking_code" className="block text-sm font-medium text-foreground">
              {t('form.details.tracking_code_label', 'Código de seguimiento')}
            </Label>
            <Input
              id="tracking_code"
              type="text"
              placeholder={t('form.details.tracking_code_placeholder', 'Ej: TRACK-12345')}
              data-testid="entry-tracking-code-input"
              disabled={disabled || isLoading}
              autoComplete="off"
              {...register('tracking_code')}
            />
            {errors.tracking_code && (
              <p className="text-sm text-destructive" data-testid="tracking-code-error">
                {errors.tracking_code.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

EntryDetailsForm.displayName = 'EntryDetailsForm';

export { EntryDetailsForm };
export default EntryDetailsForm;
